import type { Record } from "@/pages/GovernancePage/applications/policies/getDemoData.types"

export interface PolicyStore {
  records: Record[]
}

type Listener = () => void

// The AI Policies demo keeps its records in sessionStorage, per tenant, so a
// page refresh during a session keeps the policies the user created. Every
// provider on the page (list page, agent drawer, agents table) reads the same
// in-memory copy and re-renders when any of them commits.
const SESSION_STORE_KEY = "ai_policy_session_store_v4"
const storeKey = (company: string) => `${SESSION_STORE_KEY}_${company}`
const cache = new Map<string, PolicyStore | undefined>()
const listeners = new Set<Listener>()

const readSession = (company: string): PolicyStore | undefined => {
  try {
    const raw = window.sessionStorage.getItem(storeKey(company))

    if (!raw) return undefined

    const parsed: Partial<PolicyStore> = JSON.parse(raw)

    if (!Array.isArray(parsed.records)) return undefined

    return { records: parsed.records }
  } catch {
    return undefined
  }
}

export const readStore = (company: string): PolicyStore | undefined => {
  if (!cache.has(company)) cache.set(company, readSession(company))

  return cache.get(company)
}

export const writeStore = (company: string, store: PolicyStore) => {
  cache.set(company, store)
  try {
    window.sessionStorage.setItem(storeKey(company), JSON.stringify(store))
  } catch {
    // Storage can be unavailable (private mode); the in-memory copy still works.
  }
  listeners.forEach((listener) => listener())
}

export const subscribeStore = (listener: Listener) => {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}
